import {
    Button,
    ButtonProps,
    Popover as MuiPopover,
    PopoverProps as MuiPopoverProps
} from '@mui/material'
import React, {
    Children,
    cloneElement,
    isValidElement,
    ReactNode,
    useState,
    MouseEvent
} from 'react'

export type PopoverRenderProps = {
    onClose: (
        event: Record<string, unknown> | MouseEvent,
        reason: `backdropClick` | `escapeKeyDown`
    ) => void
}

export type PopoverProps = Omit<MuiPopoverProps, `open`> & {
    /**
     * If `true`, the component is shown.
     */
    open?: boolean
    /**
     * Props passed to the internal button component.
     */
    buttonProps?: ButtonProps
    /**
     * Label to be rendered in the popover button.
     */
    buttonLabel?: string
    /**
     * Children to be rendered in the popover.
     */
    children: ReactNode | ((props: PopoverRenderProps) => ReactNode)
    /**
     * Button component to be rendered instead of the default one. This overrides
     * the default behavior of rendering a `button` with the `buttonLabel`.
     */
    buttonComponent?: ReactNode
}

function Popover({
    buttonProps,
    buttonLabel,
    buttonComponent,
    children,
    id,
    anchorEl,
    open,
    ...props
}: PopoverProps) {
    const [innerAnchorEl, setInnerAnchorEl] = useState<HTMLButtonElement>()
    const innerOpen = Boolean(innerAnchorEl)

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        setInnerAnchorEl(event.currentTarget)
    }

    function handleClose(
        event: Record<string, unknown> | MouseEvent,
        reason: `backdropClick` | `escapeKeyDown`
    ) {
        setInnerAnchorEl(undefined)

        if (props.onClose) {
            props.onClose(event, reason)
        }
    }

    return (
        <>
            {buttonComponent ? (
                Children.map<ReactNode, ReactNode>(buttonComponent, (child) => {
                    if (!isValidElement(child)) {
                        return null
                    }

                    return cloneElement(child, {
                        ...child.props,
                        onClick: (event: MouseEvent<HTMLButtonElement>) => {
                            if (child.props.onClick) {
                                child.props.onClick(event)
                            }

                            handleClick(event)
                        }
                    })
                })
            ) : (
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleClick}
                    {...buttonProps}
                >
                    {buttonLabel}
                </Button>
            )}

            <MuiPopover
                onClose={handleClose}
                open={open || innerOpen}
                anchorEl={anchorEl || innerAnchorEl}
                {...props}
            >
                {typeof children === `function`
                    ? children({ onClose: handleClose })
                    : children}
            </MuiPopover>
        </>
    )
}

export default Popover
